/**
 * `multi_choice` renderer — composes `@nextday/funnel-ui` `MultiChoice`
 * (with the E2 extension props: `minSelections` / `maxSelections` /
 * `helperText` / `shakeOnMaxReached`).
 *
 * Stores the selected option ids array under `data.answerKey`. Multi-choice
 * does NOT auto-advance — the user has to commit via the global `Continue`
 * button. The orchestrator gates Continue on validity, which it computes
 * from `data.minSelections` against `answers[answerKey]?.length`.
 *
 * Step JSON shape (plan §5 step 3):
 *
 *   {
 *     "type": "multi_choice",
 *     "data": {
 *       "question": "Pick the vibe you want.",
 *       "description": "Choose up to 3.",
 *       "helperText": "Choose up to 3.",
 *       "minSelections": 1,
 *       "maxSelections": 3,
 *       "shakeOnMaxReached": true,
 *       "answerKey": "vibe_tags",
 *       "options": [
 *         { "id": "warm", "label": "Warm", "description": "..." },
 *         ...
 *       ]
 *     }
 *   }
 */
import { MultiChoice } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import { choiceOptionSchema } from "@survey-kit/renderers/choiceOptionSchema";
import type { RendererProps } from "@survey-kit/types";

export const multiChoiceSchema = z
  .object({
    question: z.string().min(1),
    description: z.string().optional(),
    eyebrow: z.string().optional(),
    options: z.array(choiceOptionSchema).min(2),
    /** State key under which the selected option id array is recorded. */
    answerKey: z.string().min(1),
    minSelections: z.number().int().nonnegative().optional(),
    maxSelections: z.number().int().positive().optional(),
    helperText: z.string().optional(),
    shakeOnMaxReached: z.boolean().optional(),
    swapOnMax: z.boolean().optional(),
    layout: z.enum(["row", "tile", "chip"]).optional(),
    columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    /** When false, hides the checkbox indicator on row layout. Defaults to true. */
    showIndicator: z.boolean().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.maxSelections === undefined ||
      value.minSelections === undefined ||
      value.maxSelections >= value.minSelections,
    { message: "maxSelections must be ≥ minSelections" },
  );

export type MultiChoiceData = z.infer<typeof multiChoiceSchema>;

const MultiChoiceRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onValidityChange,
}: RendererProps<MultiChoiceData>) => {
  const initial = Array.isArray(answers[data.answerKey])
    ? (answers[data.answerKey] as ReadonlyArray<unknown>).filter(
        (entry): entry is string => typeof entry === "string",
      )
    : [];

  return (
    <MultiChoice
      question={data.question}
      description={data.description}
      eyebrow={data.eyebrow}
      options={data.options}
      initialValue={initial}
      minSelections={data.minSelections}
      maxSelections={data.maxSelections}
      helperText={data.helperText}
      shakeOnMaxReached={data.shakeOnMaxReached}
      swapOnMax={data.swapOnMax}
      layout={data.layout}
      columns={data.columns}
      showIndicator={data.showIndicator}
      onChange={(ids) => onAnswer(data.answerKey, { value: ids })}
      onValidityChange={onValidityChange}
      testId={`renderer-multi-choice-${step.id}`}
    />
  );
};

export const multiChoiceRenderer = createRenderer({
  type: "multi_choice",
  dataSchema: multiChoiceSchema,
  label: "Multi choice",
  Component: MultiChoiceRenderer,
});

export default multiChoiceRenderer;
